import * as React from "react";
import Excel from "exceljs";
import {
  Alert,
  Button,
  Form,
  ModalBody,
  Spinner,
  Stack,
} from "react-bootstrap";
import {
  InvoiceImportData,
  InvoiceFields,
} from "../services/API/InvoiceReports";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport } from "@fortawesome/free-solid-svg-icons";
import {
  WithTranslation,
  useTranslation,
  withTranslation,
} from "react-i18next";
import {
  parseDate_ddMMyyyy,
  parseDate_yyyyMMdd,
  parseDate_dMyyyy,
} from "../lib/utils";
import { dateFormats } from "../constants/constants";
import { StyledModal } from "./modal/StyledModal";
import { ProjectInvoice } from "../pages/KickbackProjectReportPage";
import { FileUploadErrorContent } from "./FileUploadErrorContent";


export interface IFileUploadInputProps extends WithTranslation {
  onChange(valuesFromFile: InvoiceImportData[] | ProjectInvoice[]): void 
  invoiceFormat:InvoiceFormat;
  
}
export type InvoiceFormat = "Standard" | "Project" | "Article" | "ProjectArticle"
type CorrupData ={
  rows:any[];
  fileName:string;
  curruptRows:boolean;
}
export interface IFileUploadInputState {
  loading: boolean;
  errorMsg: string;
  open: boolean;
  selectedDateFormat: string;
  selectedFile: FileList | undefined;
  corruptData:CorrupData;
}

class FileUploadInput extends React.Component<
  IFileUploadInputProps,
  IFileUploadInputState
> {
  constructor(props: IFileUploadInputProps) {
    super(props);

    this.state = {
      loading: false,
      errorMsg: "",
      open: false,
      selectedDateFormat: "",
      selectedFile: undefined,
      corruptData:{curruptRows:false,fileName:'',rows:[]}
    };
  }

  private headersWithArticles = [
    "supplier",
    "supplierOrgId",
    "customer",
    "customerOrgId",
    "contractId",
    "invoiceNumber",
    "billingDate",
    "articleNumber",
    "articleText",
    "articleQuantity",
    "articleQuantityType",
    "articlePricePerUnit",
    "rowSumExclMoms"
  ];
  private headersWithoutArticles = [
    "supplier",
    "supplierOrgId",
    "customer",
    "customerOrgId",
    "contractId",
    "invoiceNumber",
    "billingDate",
    "sumExclMoms",
  ]
  private projectInvoiceHeaders = [
    "projectName",
    "customerName",
    "customerOrgId",
    "contractId",
    "invoiceNumber",
    "totalExclMoms",
    "billingDate",
    "reference",
    "tag",
    "projectEndDate",
    "comment"
  ]
  //projectName
  // ;customerName;
  // customerOrgId;
  // contractId;
  // invoiceNumber;
  // totalExclMoms;
  // billingDate;
  // reference;
  // tag;
  // projectEndDate;
  // comment;
  // rowSumExclMoms;
  // articleQuantity;
  // articlePricePerUnit;
  // articleNumber;
  // articleText;
  // articleQuantityType;
  private projectInvoiceHeadersWithArticles = [
    "projectName",
    "customerName",
    "customerOrgId",
    "contractId",
    "invoiceNumber",
    "totalExclMoms",
    "billingDate",
    "reference",
    "tag",
    "projectEndDate",
    "comment",
    "rowSumExclMoms",
    "articleQuantity",
    "articlePricePerUnit",
    "articleNumber",
    "articleText",
    "articleQuantityType"
  ]

  private getHeaders = () => {
    return {
      "Standard": this.headersWithoutArticles,
      "Project": this.projectInvoiceHeaders,
      "Article": this.headersWithArticles,
      "ProjectArticle": this.projectInvoiceHeadersWithArticles
    }[this.props.invoiceFormat]
  }

  private applyRegexOnCustomerOrgId = (orgId: string)  => {
    if(!orgId)
      return "";
    const match = orgId.match(/^SE(.*)01$/);
    if(match){
      return match[1]
    }
    return orgId;
  }
  private finalCheckBeforeReturn = (
  rows: any,
  arrayData: string[]
): { error: boolean; values: Array<{ id: number; rawData: string; reasons: string[]; parsedIndex?: number }> } => {

  const headers = this.getHeaders() ?? [];
  const expectedCols = headers.length;

  // Samma heuristik som i readCsv
  const delimiter = arrayData[0]?.split(';').length > expectedCols - 1 ? ';' : ',';

  const countSeparatorsOutsideQuotes = (line: string): number => {
    let inQuotes = false;
    let count = 0;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        // Hantera "" som escape
        if (inQuotes && line[i + 1] === '"') { i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === delimiter && !inQuotes) {
        count++;
      }
    }
    return count;
  };

  const structuralReasons = (line: string): string[] => {
    const reasons: string[] = [];
    if (expectedCols > 0) {
      const sep = countSeparatorsOutsideQuotes(line);
      console.log(line)
      if (sep + 1 !== expectedCols) {
        reasons.push(`fel antal kolumner: ${sep + 1} (förväntat ${expectedCols})`);
      }
    }
    const q = (line.match(/"/g) || []).length;
    if (q % 2 !== 0) reasons.push('obalanserade citattecken (ojämnt antal ")');
    // if (delimiter === ';' && /;;/.test(line)) reasons.push('flera separatorer i följd (;;)');
    // valfritt: kontrolltecken
    if (/[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(line)) reasons.push('rad innehåller kontrolltecken');
    return reasons;
  };

  const valueReasonsForRow = (obj: any): string[] => {
    const reasons: string[] = [];
    const dfs = (o: any, path = '') => {
      if (!o || typeof o !== 'object') return;
      for (const [k, v] of Object.entries(o)) {
        if (k === '__rawIndex' || k === '__rawLine') continue;
        const p = path ? `${path}.${k}` : k;
        if (v && typeof v === 'object') {
          dfs(v, p);
        } else if (typeof v === 'string' && (v === 'error' || v === 'Invalid Date')) {
          reasons.push(v === 'Invalid Date' ? `ogiltigt datum i "${p}"` : `felvärde i "${p}"`);
        }
      }
    };
    dfs(obj);
    return reasons;
  };

  const result: Array<{ id: number; rawData: string; reasons: string[]; parsedIndex?: number }> = [];
  const seen = new Set<number>();

  let lastGoodRaw = -1;

  for (let parsedIndex = 0; parsedIndex < rows.length; parsedIndex++) {
    const r = rows[parsedIndex];
    const rawIndex = Number.isInteger(r?.__rawIndex) ? r.__rawIndex : parsedIndex; // fallback
    const rawLine = r?.__rawLine ?? arrayData[rawIndex] ?? '';

    const reasons = [
      ...valueReasonsForRow(r),
      ...structuralReasons(rawLine)
    ];

    // Ingen indikation på fel → uppdatera "sista korrekta"
    if (reasons.length === 0) {
      lastGoodRaw = rawIndex;
      continue;
    }

    // Vid första fel i en följd: ta med sista korrekta rad före felet
    if (lastGoodRaw >= 0 && !seen.has(lastGoodRaw)) {
      result.push({
        id: lastGoodRaw,
        rawData: arrayData[lastGoodRaw],
        reasons: ['sista korrekta rad innan fel']
      });
      seen.add(lastGoodRaw);
    }

    if (!seen.has(rawIndex)) {
      result.push({
        id: rawIndex,
        rawData: arrayData[rawIndex],
        reasons,
        parsedIndex
      });
      seen.add(rawIndex);
    }
  }

  return { error: result.length > 0, values: result };
};

  // private finalCheckBeforeReturn = (rowData:any,arrayData:string[]):{error:boolean,values:any} =>{
  //   /*LÄGG TILL ETT BYPASS OPTION för användaren om den vil ignorera felhanteringen och hoppa över den och läsa in filen ändå för att justera den i gränssnittet*/
  //   /*titta igenom hela arrayen steg för steg
  //     när jag väl stöter på ett error så vill jag lägga till det som kom innan också
  //     jag sparar resultatet i en array id och en error flagga
  //   */ 
  
  //   const result:any[] = [];
  //   let arrayLength = rowData.length; 
  //   let slowPointer = 0; //slow ligger ett steg bakom för att ha referens till den rad som kom innan
  //   let currentIndex = 0; //current är current
  //   while (currentIndex < arrayLength) {
  //     const currObjKeys = Object.keys(rowData[currentIndex]);
  //     for(let i = 0;i < currObjKeys.length;i++){
  //         if(rowData[currentIndex][currObjKeys[i]] == 'error' || rowData[currentIndex][currObjKeys[i]] == 'Invalid Date'){
  //           //här har något gått fel
            
  //           if(!result.find(x => x.id == slowPointer)){
  //             result.push({id:slowPointer,rawData:rowData[slowPointer]});
  //           }
  //           result.push({id:currentIndex,rawData:rowData[currentIndex]});
  //           console.log(currentIndex) 
  //           break;
  //         }
  //     }
  //     currentIndex++;
  //     slowPointer = currentIndex == 0 ? 0 : currentIndex -1;
  //   }
  //   console.log('resultat från check',result);
  //   return {error:result.length > 1,values:result};
  // }
  private mapRow = (rowData: any, index: number) => {
  const meta = { __rawIndex: rowData._rawIndex, __rawLine: rowData._rawData };

  return {
    "Standard": (): InvoiceImportData => ({
      id: -1,
      groupId: index,
      billingDate: this.parseSelectedDateFormatting(rowData.billingDate) ?? 'error',
      contractId: rowData.contractId ?? 'error',
      customer: rowData.customer ?? 'error',
      customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId) ?? 'error',
      invoiceNumber: rowData.invoiceNumber ?? 'error',
      rowSumExclMoms: rowData.sumExclMoms ?? 'error',
      articleQuantity: "",
      articlePricePerUnit: "",
      articleNumber: "",
      articleText: "",
      articleQuantityType: "",
      ...meta
    }),
    "Project": (): ProjectInvoice => ({
      uniqueRowId: Math.random(),
      projectName : rowData.projectName ?? '',
      customerName: rowData.customerName ?? '',
      customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId),
      contractId: rowData.contractId ?? '',
      invoiceNumber: rowData.invoiceNumber,
      totalExclMoms: { value: +rowData.totalExclMoms ?? '', display: rowData.totalExclMoms, valid: false, error: false },
      billingDate: this.parseSelectedDateFormatting(rowData.billingDate ?? ''),
      reference: rowData.reference ?? '',
      tag: rowData.tag ?? '',
      projectEndDate: this.parseSelectedDateFormatting(rowData.projectEndDate ?? ''),
      comment: rowData.comment ?? '',
      isDuplicate: { value:false, backgorundColor:'' },
      supplierOrgId: '',
      ...meta
    } as any),
    "ProjectArticle": (): ProjectInvoice => ({
      uniqueRowId: Math.random(),
      projectName : rowData.projectName ?? '',
      customerName: rowData.customerName ?? '',
      customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId),
      contractId: rowData.contractId ?? '',
      invoiceNumber: rowData.invoiceNumber,
      totalExclMoms: { value: +rowData.totalExclMoms ?? '', display: rowData.totalExclMoms ?? '', valid: false, error: false },
      billingDate: this.parseSelectedDateFormatting(rowData.billingDate ?? ''),
      reference: rowData.reference ?? '',
      tag: rowData.tag ?? '',
      projectEndDate: this.parseSelectedDateFormatting(rowData?.projectEndDate ?? ''),
      comment: rowData.comment ?? '',
      isDuplicate:{ value:false, backgorundColor:'' },
      supplierOrgId:'',
      rowSumExclMoms: rowData.rowSumExclMoms ?? '',
      articleQuantity: rowData.articleQuantity ?? '',
      articlePricePerUnit: rowData.articlePricePerUnit ?? '',
      articleNumber: rowData.articleNumber ?? '',
      articleText: rowData.articleText ?? '',
      articleQuantityType: rowData.articleQuantityType ?? '',
      ...meta
    } as any),
    "Article": (): InvoiceImportData => ({
      id: -1,
      groupId: -1,
      billingDate: this.parseSelectedDateFormatting(rowData.billingDate) ?? 'error',
      contractId: rowData.contractId ?? 'error',
      customer: rowData.customer ?? 'error',
      customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId) ?? 'error',
      invoiceNumber: rowData.invoiceNumber ?? 'error',
      rowSumExclMoms: rowData.rowSumExclMoms ?? 'error',
      articleQuantity: rowData.articleQuantity ?? 'error',
      articlePricePerUnit: rowData.articlePricePerUnit ?? 'error',
      articleNumber: rowData.articleNumber ?? 'error',
      articleText: rowData.articleText ?? 'error',
      articleQuantityType: rowData.articleQuantityType ?? 'error',
      ...meta
    })
  }[this.props.invoiceFormat]();
};

  // private mapRow = (rowData: any, index: number) => {
  //   console.log('data innan mappningen till ett typat object',rowData);
  //   return {
  //     "Standard": () : InvoiceImportData => (
  //       {
  //         id: -1,
  //         groupId:index,
  //         billingDate: this.parseSelectedDateFormatting(rowData.billingDate) ?? 'error',
  //         contractId: rowData.contractId ?? 'error',
  //         customer: rowData.customer ?? 'error',
  //         customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId) ?? 'error',
  //         invoiceNumber: rowData.invoiceNumber ?? 'error',
  //         rowSumExclMoms: rowData.sumExclMoms ?? 'error',
  //         articleQuantity: "" ,
  //         articlePricePerUnit: "",
  //         articleNumber: "",
  //         articleText: "",
  //         articleQuantityType: ""
  //       }
  //     ),
  //     "Project": (): ProjectInvoice => (
  //       {
  //         uniqueRowId:Math.random(),
  //         projectName : rowData.projectName ?? '',
  //         customerName: rowData.customerName ?? '',
  //         customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId),
  //         contractId: rowData.contractId ?? '',
  //         invoiceNumber: rowData.invoiceNumber,
  //         totalExclMoms: { value: +rowData.totalExclMoms ?? '', display: rowData.totalExclMoms, valid: false ,error:false},
  //         billingDate: this.parseSelectedDateFormatting(rowData.billingDate ?? ''),
  //         reference: rowData.reference ?? '',
  //         tag: rowData.tag ?? '',
  //         projectEndDate: this.parseSelectedDateFormatting(rowData.projectEndDate ?? ''),
  //         comment: rowData.comment ?? '',
  //         isDuplicate:{value:false,backgorundColor:''},
  //         supplierOrgId:''
  //       } as ProjectInvoice
  //     ),
  //     "ProjectArticle": (): ProjectInvoice => (
  //       {
  //         uniqueRowId:Math.random(),
  //         projectName : rowData.projectName ?? '',
  //         customerName: rowData.customerName ?? '',
  //         customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId),
  //         contractId: rowData.contractId ?? '',
  //         invoiceNumber: rowData.invoiceNumber,
  //         totalExclMoms: { value: +rowData.totalExclMoms ?? '', display: rowData.totalExclMoms ?? '', valid: false ,error:false},
  //         billingDate: this.parseSelectedDateFormatting(rowData.billingDate ?? ''),
  //         reference: rowData.reference ?? '',
  //         tag: rowData.tag ?? '',
  //         projectEndDate: this.parseSelectedDateFormatting(rowData.projectEndDate ?? ''),
  //         comment: rowData.comment ?? '',
  //         isDuplicate:{value:false,backgorundColor:''},
  //         supplierOrgId:'',
  //         rowSumExclMoms: rowData.rowSumExclMoms ?? '',
  //         articleQuantity: rowData.articleQuantity ?? '',
  //         articlePricePerUnit: rowData.articlePricePerUnit ?? '',
  //         articleNumber: rowData.articleNumber ?? '',
  //         articleText: rowData.articleText ?? '',
  //         articleQuantityType: rowData.articleQuantityType ?? '',
         
  //       } as ProjectInvoice
  //     ),
  //     "Article": () : InvoiceImportData => (
  //       {
  //         id: -1,
  //         groupId:-1,
  //         billingDate: this.parseSelectedDateFormatting(rowData.billingDate) ?? 'error',
  //         contractId: rowData.contractId ?? 'error',
  //         customer: rowData.customer ?? 'error',
  //         customerOrgId: this.applyRegexOnCustomerOrgId(rowData.customerOrgId) ?? 'error',
  //         invoiceNumber: rowData.invoiceNumber ?? 'error',
  //         rowSumExclMoms: rowData.rowSumExclMoms ?? 'error',
  //         articleQuantity: rowData.articleQuantity ?? 'error',
  //         articlePricePerUnit: rowData.articlePricePerUnit ?? 'error',
  //         articleNumber: rowData.articleNumber ?? 'error',
  //         articleText: rowData.articleText ?? 'error',
  //         articleQuantityType: rowData.articleQuantityType ?? 'error'
  //       }
  //     )
  //   }[this.props.invoiceFormat]();
  // }
// Hjälpare (lägg i klassen):
private escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

private getValues = (
  headers: string[],
  data: string[],
  delimiter: string,
  csvHeaders?: string[]
): any[] => {
  // 1 = header finns, 0 = ingen header inskickad
  const headerOffset = csvHeaders ? 1 : 0;

  // Robust split: delimiter utanför citattecken
  const regex = new RegExp(`${this.escapeRegExp(delimiter)}(?=(?:[^"]*"[^"]*")*[^"]*$)`, 'g');
 
  // Bär med originalindex innan du filtrerar
  const splitData = data
    .map((row, rawIndex) => ({ rawIndex, fields: row.split(regex), rawLine: row }))
    .filter(x => !x.fields.every(str => str === ''));

  return splitData.map(({ fields, rawIndex, rawLine }, i) => {
    const rowObj: {[key:string]:any} = fields.reduce((prev: {[key:string]:any}, curr, index) => {
      const key = csvHeaders ? csvHeaders[index] : headers[index];
      const value = curr?.trim()?.replaceAll('"', '') ?? '';
      if (headers.includes(key)) {
        prev[key] = value;
      }
      return prev;
    }, {});

    // Meta för stabil mappning tillbaka till rå CSV
    rowObj._rawIndex = rawIndex + headerOffset; // absolut index i arrayData
    rowObj._rawData  = rawLine;                 // exakt radråtext

    return this.mapRow(rowObj, i);
  });
};

  // private getValues = (
  //   headers: string[],
  //   data: string[],
  //   delimiter: string,
  //   csvHeaders?: string[]
  // ):/* InvoiceImportData[] | ProjectInvoice[]*/any[] => {
  //   //Mathias dark magic
  //   const regexStr = `${delimiter}(?!(?=[^"]*"[^"]*(?:"[^"]*"[^"]*)*\$))`;
  //   const regex = new RegExp(regexStr, "gm");
  
  //   const splitData = data
  //     .map((row) => row.split(regex))
  //     .filter((row) => !row.every((str) => str == ""));
     
  //   return splitData.map((row,i) => {
     
  //     const rowData: any = row.reduce((prev, curr, index) => {
  //       const key = csvHeaders ? csvHeaders[index] : headers[index];
       
  //       const value = curr?.trim()?.replaceAll('"', "") ?? "";
       
       
  //       if ( headers.includes(key)) {
  //         return {
  //           ...prev,
  //           [key]: value,
  //           _rawIndex:i +1,
  //           _rawData:data[i]
  //         };
  //       }
  //       return prev;
  //     }, {});


  //     return this.mapRow(rowData,i);
  //   });
  // };

 private  roundToPrecision = (value:number, precision:number) => {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
}
  private readCsv = (file: File, headers: string[]) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = (event) => {
      const csvData = event?.target?.result as String;
      const csvRows = csvData
        .replace(/\r\n/g, "\n")
        .split("\n")
        .filter((row) => row.length);
      if (csvRows[0] == undefined) {
        return;
      }
      const delimiter = csvRows[0].split(";").length > headers.length-1 ? ";" : ",";
      const arrayData = csvRows.map((dataString) =>
        
        dataString.endsWith(delimiter) ? dataString.slice(0, -1) : dataString
        
      );
  
      const csvHeaders = arrayData[0].split(delimiter).map(header => header.replaceAll("\"", ""));
      
      const values =  headers.every((key) => csvHeaders.includes(key))
        ? this.getValues(headers, arrayData.slice(1), delimiter, csvHeaders)
        : this.getValues(
            headers,
            headers.some((key) => csvHeaders.includes(key))
              ? arrayData.slice(1)
              : arrayData,
            delimiter
          ); 
       //när jag är klar med allt så behöver jag lägga detta i exel också
       console.log(values)
      const res = this.finalCheckBeforeReturn(values,arrayData); 
      if(!res.error){
        this.props.onChange(values);
      }
      else{
        this.setState({open:true,corruptData:{...this.state.corruptData,rows:res.values,curruptRows:res.error}},() => console.log(this.state.corruptData));
      }
    };
  };

  private parseSelectedDateFormatting(date:string): string {
    if(date?.length == 0){
      return ''
    }
    else{
      switch (this.state.selectedDateFormat) {
      case "yyyy-MM-dd":
      const currDate = new Date(date).toLocaleDateString("sv-SE");
      return currDate
      case "dd-MM-yyyy":
        return parseDate_ddMMyyyy(date, "-");
      case "dd/MM/yyyy":
        return parseDate_ddMMyyyy(date, "/");
      case "dd.MM.yyyy":
        return parseDate_ddMMyyyy(date, ".");
      case "yyyy.MM.dd":
        return parseDate_yyyyMMdd(date, ".");
      case "yyyy MM dd":
        return parseDate_yyyyMMdd(date, " ");
      case "d/M/yyyy":
        return parseDate_dMyyyy(date, "/");
      case "d.M.yyyy":
        return parseDate_dMyyyy(date, ".");

      default:
        break;
    }
    return date;
    }
    
  }

  private readExcel = async (files: FileList, workbook: Excel.Workbook, headers: string[]) => {
    const y = await files[0].arrayBuffer();
    await workbook.xlsx.load(y);
   
    const ws = workbook.worksheets[0];
    let excelHeaders: string[] = [];
    ws.getRow(1).eachCell((cell) => excelHeaders.push(cell.text));
   
    const delimiter = ";";
    const rows = ws.getRows(1, ws.rowCount);
    if (rows !== undefined) {
      const rowsData = rows.map((row) =>
        (row.values as []).filter((e) => e !== undefined).join(delimiter)
    );

      const values = headers.every((key) => excelHeaders.includes(key))
        ? this.getValues(
            headers,
            rowsData.slice(1),
            delimiter,
            excelHeaders
          )
        : this.getValues(
            headers,
            headers.some((key) => excelHeaders.includes(key))
              ? rowsData.slice(1)
              : rowsData,
            delimiter
          );
         
      this.props.onChange(values);
    }
  };
  private handleModalCLose() {
    this.setState({
      open: false,
      selectedFile: undefined,
      selectedDateFormat: "",
      corruptData:{curruptRows:false,fileName:'',rows:[]}
    });
  }
  private generateDateFormats = () => {
    const res: any[] = [];
    dateFormats.map((x, index) => {
      res.push(
        <option key={index} value={x}>
          {x}
        </option>
      );
    });
    return res;
  };
  private handleDateSelection(index: any) {
    this.setState({ selectedDateFormat: index });
  }
  private fileInput: HTMLInputElement = document.createElement("input");

  public render() {
    return (
      
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Button
          className="btn button-submit"
          style={{color:'#173d54'}}
          disabled={this.state.loading}
          onClick={() => this.setState({ open: true })}
        >
          {this.state.loading ? (
            <Spinner />
          ) : (
            <>
              <FontAwesomeIcon icon={faFileImport} style={{ marginRight: 8 }} />
              {this.props.t("invoiceReport.importFile")}
            </>
          )}
        </Button>
        {this.state.open && (
          <StyledModal
          headerStyle={{backgroundColor:'#173c53',color:'white'}}
            onDismiss={() => this.handleModalCLose()}
            title={this.props.t(
              "invoiceReport.importFile"
            ).toString()} /*closeOnClickOutside={true} onClose={()=> this.handleModalCLose()}*/
            body={
              <>
              {this.state.corruptData.curruptRows ? <FileUploadErrorContent corruptedRows={this.state.corruptData.rows} fileName={this.state.corruptData.fileName} onClose={()=> this.handleModalCLose()} onRetry={() => console.log()}/>:
                <div>
                <Stack className="submit-modal-body" gap={3}>
                  <Form.Group
                    className="mb-3"
                    controlId="exampleForm.ControlInput1"
                  >
                    <Form.Label>
                      {this.props.t("invoiceReport.chooseFileToUpload")}:
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                        const files: FileList = ev.target.files as FileList;
                        if (files.length > 0)
                          this.setState({ selectedFile: files });
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{this.props.t("invoiceReport.chooseDateFormat")}</Form.Label>
                    <Form.Select
                      // style={{ textTransform: "capitalize" }}
                     defaultValue={' '}
                      value={this.state.selectedDateFormat}
                      onChange={(ev) => {
                        this.handleDateSelection(ev.target.value);
                      }}
                    >
                      <option>{this.props.t("invoiceReport.noDateFormat")}</option>
                      {this.generateDateFormats()}
                    </Form.Select>
                  </Form.Group>
                  {this.state.errorMsg && typeof this.state.selectedFile === 'undefined' &&(
                    <div className="error-message">
                      <span>{this.props.t("general.errorMsg")}:</span>
                      {this.state.errorMsg}
                    </div>
                  )}
                </Stack>
              </div>
              }
              </>
            }
            footer={
              <div >
                <Stack direction="horizontal" gap={3}>
                  {!this.state.corruptData.curruptRows && 
                    <Button
                  className="button"
                 
                  disabled={this.state.loading}
                  onClick={async (ev) => {
                    this.setState({
                      loading: true,
                      errorMsg: "",
                    });
                    if(typeof this.state.selectedFile === 'undefined'){
                      this.setState({
                        loading:false,
                        errorMsg:this.props.t('invoiceReport.importErrorMsg')
                      })
                    }
                      else{
                        
                    const test: unknown = this.state.selectedFile;
                    const files: FileList = test as FileList;
                    const workbook = new Excel.Workbook();
                    
                    const file = files[0];
                    try {
                      //(file.type == "application/vnd.ms-excel" && file.name.endsWith(".csv")) => "text/csv" in disguise
                     
                      if (
                        file.type == "text/csv" ||
                        (file.type == "application/vnd.ms-excel" &&
                          file.name.endsWith(".csv"))
                      ) {
                        this.setState({corruptData:{...this.state.corruptData,fileName:file.name}})
                        this.readCsv(file, this.getHeaders());
                      } else {
                        await this.readExcel(files, workbook, this.getHeaders());
                      }
                      this.setState({
                        loading: false,
                        open:false
                      });
                     
                    
                    } catch (error) {
                      console.error(error);
                      this.setState({
                        loading: false,
                        errorMsg: this.props.t("invoiceReport.importErrorMsg"),
                      });
                    }
                      }
                    
                  }}
                >
                  {this.state.loading ? (
                    <Spinner />
                  ) : (
                    <> <FontAwesomeIcon
                    icon={faFileImport}
                    style={{ marginRight: 8 }}
                  />
                  {this.props.t("invoiceReport.uploadFile")}
                
                </>
                   
                  )}
                </Button>
                  }
                
                <Button className="button-cancel" onClick={()=> this.handleModalCLose()}>{this.props.t('invoiceReport.close')}</Button>   
                </Stack>                
              </div>
            }
          />
        )}
      </div>
    );
  }
}
export default withTranslation()(FileUploadInput);


